import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Label2, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";

const CATS = ["Allgemein", "Handbuch", "Zertifikat", "Foto", "Protokoll", "Datenblatt"];

export default function Documents() {
  const [docs, setDocs] = useState(null);
  const [assets, setAssets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "Allgemein", asset_id: "", content: "" });

  const load = useCallback(() => {
    api.get("/documents").then((r) => setDocs(r.data));
    api.get("/assets").then((r) => setAssets(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      await api.post("/documents", { ...form, asset_id: form.asset_id || null });
      toast.success("Dokument gespeichert");
      setOpen(false); setForm({ name: "", category: "Allgemein", asset_id: "", content: "" }); load();
    } catch (e) { toast.error("Fehler", { description: formatApiError(e.response?.data?.detail) }); }
  };

  if (!docs) return <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-md" />)}</div>;
  const assetName = (id) => assets.find((a) => a.id === id)?.name;

  return (
    <div>
      <PageHeader title="Dokumente" subtitle={`${docs.length} Dokumente · versioniert & klassifiziert`}>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-document-button" className="gap-2"><Plus size={18} weight="bold" />Neues Dokument</Button></DialogTrigger>
          <DialogContent data-testid="document-dialog">
            <DialogHeader><DialogTitle className="font-heading">Dokument erfassen</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5"><Label2>Name *</Label2><Input data-testid="document-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label2>Kategorie</Label2>
                <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label2>Verknüpfte Anlage</Label2>
                <Select value={form.asset_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, asset_id: v === "none" ? "" : v }))}>
                  <SelectTrigger><SelectValue placeholder="Keine" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Keine</SelectItem>{assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label2>Inhalt / Notiz</Label2><Textarea value={form.content} onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))} /></div>
            </div>
            <DialogFooter><Button data-testid="document-save" onClick={submit} disabled={!form.name}>Speichern</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>
      <Card className="divide-y divide-border rounded-md border">
        {docs.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">Noch keine Dokumente.</div>}
        {docs.map((d) => (
          <div key={d.id} data-testid="document-row" className="flex items-center justify-between gap-3 px-5 py-4">
            <div className="flex min-w-0 items-center gap-3">
              <FileText size={20} weight="duotone" className="shrink-0 text-primary" />
              <div className="min-w-0">
                <div className="truncate font-semibold">{d.name} <span className="text-xs font-normal text-muted-foreground">v{d.version}</span></div>
                <div className="text-xs text-muted-foreground">{assetName(d.asset_id) || "—"} · {d.uploaded_by}</div>
              </div>
            </div>
            <StatusBadge tone="blue">{d.category}</StatusBadge>
          </div>
        ))}
      </Card>
    </div>
  );
}
